import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { Trophy, MapPin, CalendarDays, Shield, ChevronRight } from "lucide-react";

export default function TournamentPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/live/tournaments/${id}`).then(({ data }) => setD(data)).catch(() => setErr(true)); }, [id]);

  if (err) return <Wrap><div className="py-20 text-center text-slate-400">Tournament not found.</div></Wrap>;
  if (!d) return <Wrap><div className="py-20 text-center text-slate-400">Loading…</div></Wrap>;
  const t = d.tournament;

  return (
    <Wrap>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="label-overline text-[#E63946] flex items-center gap-2"><Trophy className="w-4 h-4" /> Tournament</div>
          <h1 data-testid="tournament-name" className="font-display text-4xl uppercase tracking-tighter mt-1">{t.name}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/60 mt-2">
            {t.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.location}</span>}
            {t.dates && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{t.dates}</span>}
            {t.age_division && <span>{t.age_division}</span>}
          </div>
          {d.champion && (
            <div data-testid="tournament-champion" className="mt-4 inline-flex items-center gap-2 bg-[#E63946] px-4 py-2 rounded-sm">
              <Trophy className="w-4 h-4" /> <span className="font-semibold uppercase tracking-wider text-sm">Champions: {d.champion.name}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-8">
        <section>
          <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Teams ({d.teams.length})</h2>
          <div className="space-y-2" data-testid="tournament-teams">
            {d.teams.map((tm) => (
              <Link key={tm.id} to={`/live/teams/${tm.slug}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm p-2.5 hover:border-[#E63946]">
                <div className="w-8 h-8 rounded-sm bg-slate-100 grid place-items-center shrink-0 overflow-hidden">{tm.logo_url ? <img src={tm.logo_url} alt="" className="w-full h-full object-cover" /> : <Shield className="w-4 h-4 text-slate-300" />}</div>
                <span className="text-sm font-semibold flex-1 truncate">{tm.name}</span>
                {tm.id === t.champion_team_id && <Trophy className="w-4 h-4 text-[#E63946]" />}
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </Link>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Games ({d.games.length})</h2>
          <div className="space-y-2" data-testid="tournament-games">
            {d.games.map((g) => (
              <div key={g.id} className="bg-white border border-slate-200 rounded-sm p-2.5 text-sm">
                <div className="flex items-center justify-between text-[11px] text-slate-400 uppercase tracking-wider mb-1">
                  <span>{(g.date || "").slice(5)}</span><span>{(g.playoff_round || g.game_type || "").replace(/_/g, " ")}</span>
                </div>
                <div className="flex justify-between"><span className="truncate">{g.home_name}</span><span className="font-data">{g.score?.home ?? 0}</span></div>
                <div className="flex justify-between text-slate-500"><span className="truncate">{g.away_name}</span><span className="font-data">{g.score?.away ?? 0}</span></div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Wrap>
  );
}
const Wrap = ({ children }) => <div className="min-h-screen bg-[#F1F3F6]"><Header />{children}</div>;
